import * as React from "react"
import { forwardRef } from "react"
import { Input as ShadInput } from "@/components/ui/input"
import { cn } from "@/lib/utils"

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  inputSize?: "sm" | "md" | "lg"
  variant?: "default" | "ghost" | "glass"
  fullWidth?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      leftIcon,
      rightIcon,
      className,
      id,
      inputSize = "md",
      variant = "default",
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s/g, "-")

    const sizeClasses = {
      sm: "h-8 px-3 text-xs",
      md: "h-10 px-3 text-sm",
      lg: "h-12 px-4 text-base",
    }

    const variantClasses = {
      default: cn(
        "bg-white dark:bg-surface-900/50",
        "border border-surface-200 dark:border-surface-700",
        "hover:border-surface-300 dark:hover:border-surface-600",
        "focus-visible:ring-2 focus-visible:ring-primary-500/20"
      ),
      ghost: cn(
        "bg-transparent border border-transparent",
        "hover:bg-surface-100 dark:hover:bg-surface-800/50",
        "focus-visible:ring-2 focus-visible:ring-primary-500/20"
      ),
      glass: cn(
        "bg-surface-100/80 dark:bg-surface-800/50 backdrop-blur-sm",
        "border border-surface-200 dark:border-surface-700",
        "hover:border-surface-300 dark:hover:border-surface-600",
        "focus-visible:ring-2 focus-visible:ring-primary-500/30"
      ),
    }

    return (
      <div className={cn("flex flex-col gap-1.5", fullWidth && "w-full")}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-xs font-medium text-surface-600 dark:text-surface-400"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-surface-400 dark:text-surface-500">
              {leftIcon}
            </div>
          )}

          <ShadInput
            ref={ref}
            id={inputId}
            className={cn(
              // base
              "w-full rounded-xl",
              "text-surface-900 dark:text-surface-100",
              "placeholder-surface-400 dark:placeholder-surface-500",
              "transition-all duration-200",

              // size + variant
              sizeClasses[inputSize],
              variantClasses[variant],

              // error overrides
              error &&
                "border-danger-500 focus-visible:ring-danger-500/30 focus-visible:border-danger-500",

              // icon padding
              leftIcon && "pl-11",
              rightIcon && "pr-11",

              className
            )}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 dark:text-surface-500">
              {rightIcon}
            </div>
          )}
        </div>

        {error && (
          <p className="mt-2 flex items-center gap-1 text-sm text-danger-500">
            <svg
              className="h-4 w-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = "Input"
